import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMetaTags } from '../../hooks/useMetaTags';
import ExternalLink from '../common/ExternalLink';
import { emailConfig } from '../../config/emailConfig';

/**
 * PrivacyPolicy component displays the application's privacy policy.
 */
function PrivacyPolicy() {
  const { t, i18n } = useTranslation('privacy');
  const { t: commonT } = useTranslation();
  const [readingTime] = useState(8); // Default estimated reading time in minutes
  
  // Set the meta tags for this page
  useMetaTags(
    t('metaTitle', 'Privacy Policy - TapDonkey'),
    t('metaDescription', 'Privacy Policy for TapDonkey, describing how we handle your data and protect your privacy.')
  );

  // Date formatting based on user locale
  const formatDate = (date: Date) => {
    try {
      return date.toLocaleDateString(i18n.language || navigator.language || 'en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }
  };

  // Generate anchor links for table of contents
  const generateAnchorId = (sectionNumber: number) => `privacy-section-${sectionNumber}`;

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-12 col-lg-10">
          <h1 className="mb-4">{t('title', 'Privacy Policy')}</h1>
          
          <div className="card shadow-sm rounded-4 mb-4">
            <div className="card-body p-4 p-md-5">
              <div className="d-flex justify-content-between align-items-center mb-4">
                                <p className="text-body-secondary small mb-0">                  {commonT('common.lastUpdated', 'Last updated')}: {formatDate(new Date('2025-05-15'))}                </p>
                <p className="text-body-secondary small mb-0">
                  <i className="bi bi-clock me-1"></i>
                  {commonT('common.readingTime', 'Reading time')}: ~{readingTime} {commonT('common.minutes', 'minutes')}
                </p>
              </div>
              
              <div className="mb-4 p-3 bg-body-tertiary border rounded-3">
                <h2 className="h6 fw-bold mb-3">{commonT('common.tableOfContents', 'Table of Contents')}</h2>
                <ol className="list-unstyled mb-0">
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(1)}`} className="text-decoration-none link-body-emphasis">
                      1. {t('introduction.title', 'Introduction')}
                    </a>
                  </li>
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(2)}`} className="text-decoration-none link-body-emphasis">
                      2. {t('collection.title', 'Information Collection and Use')}
                    </a>
                  </li>
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(3)}`} className="text-decoration-none link-body-emphasis">
                      3. {t('use.title', 'Use of Data')}
                    </a>
                  </li>
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(4)}`} className="text-decoration-none link-body-emphasis">
                      4. {t('thirdParty.title', 'Third-Party Service Providers')}
                    </a>
                  </li>
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(5)}`} className="text-decoration-none link-body-emphasis">
                      5. {t('retention.title', 'Data Retention')}
                    </a>
                  </li>
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(6)}`} className="text-decoration-none link-body-emphasis">
                      6. {t('transfer.title', 'Transfer of Data')}
                    </a>
                  </li>
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(7)}`} className="text-decoration-none link-body-emphasis">
                      7. {t('disclosure.title', 'Disclosure of Data')}
                    </a>
                  </li>
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(8)}`} className="text-decoration-none link-body-emphasis">
                      8. {t('security.title', 'Security of Data')}
                    </a>
                  </li>
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(9)}`} className="text-decoration-none link-body-emphasis">
                      9. {t('rights.title', 'Your Data Protection Rights')}
                    </a>
                  </li>
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(10)}`} className="text-decoration-none link-body-emphasis">
                      10. {t('gdpr.title', 'GDPR Data Protection Rights')}
                    </a>
                  </li>
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(11)}`} className="text-decoration-none link-body-emphasis">
                      11. {t('children.title', 'Children\'s Privacy')}
                    </a>
                  </li>
                  <li className="mb-2">
                    <a href={`#${generateAnchorId(12)}`} className="text-decoration-none link-body-emphasis">
                      12. {t('changes.title', 'Changes to This Privacy Policy')}
                    </a>
                  </li>
                  <li className="mb-0">
                    <a href={`#${generateAnchorId(13)}`} className="text-decoration-none link-body-emphasis">
                      13. {t('contact.title', 'Contact Us')}
                    </a>
                  </li>
                </ol>
              </div>

                            <div className="alert alert-info mb-4">                <p className="small mb-0">                  <strong>{commonT('common.versionHistory', 'Version History')}:</strong> {t('versionInfo', 'This is version 1.1 of our Privacy Policy, effective from {{date}}', { date: formatDate(new Date('2025-05-15')) })}                </p>              </div>

              <section id={generateAnchorId(1)} className="mb-4">
                <h2 className="h5 fw-bold">1. {t('introduction.title', 'Introduction')}</h2>
                <p>
                  {t('introduction.p1', 'TapDonkey ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how your personal information is collected, used, and disclosed when you use our web application.')}
                </p>
                <p>
                  {t('introduction.p2', 'By using TapDonkey, you agree to the collection and use of information in accordance with this policy.')}
                </p>
              </section>

              <section id={generateAnchorId(2)} className="mb-4">
                <h2 className="h5 fw-bold">2. {t('collection.title', 'Information Collection and Use')}</h2>
                <p>
                  {t('collection.p1', 'We collect several different types of information for various purposes to provide and improve our service to you.')}
                </p>
                
                <h3 className="h6 fw-bold mt-3">2.1 {t('collection.personal.title', 'Personal Data')}</h3>
                <p>
                  {t('collection.personal.p1', 'We may ask for your email address when you create a counter with the email notification option. This is entirely optional and only used to send notifications about your counter. We do not use this email for marketing purposes.')}
                </p>
                <p>
                  {t('collection.personal.p2', 'Please note that when you provide an email address for counter notifications, it will be processed by our email service provider to deliver these notifications. The email is not stored on our servers after the notification is sent. This is necessary for us to provide the notification service you have requested.')}
                </p>
                
                <h3 className="h6 fw-bold mt-3">2.2 {t('collection.usage.title', 'Usage Data')}</h3>
                <p>
                  {t('collection.usage.p1', 'We may collect information about how you access and use the application ("Usage Data"). This may include information such as your device\'s IP address, browser type, browser version, the pages you visit, the time and date of your visit, and other diagnostic data.')}
                </p>
                
                <h3 className="h6 fw-bold mt-3">2.3 {t('collection.storage.title', 'Local Storage and Analytics')}</h3>
                <p>
                  {t('collection.storage.p1', 'We use local storage on your device to store certain preferences, such as your theme choice, language preference, and user type. This data remains on your device and is not transmitted to our servers.')}
                </p>
                <p>
                  {t('collection.storage.p2', 'We use Vercel Analytics, a privacy-focused analytics platform, to collect anonymous usage data. Vercel Analytics does not use cookies and does not track users across websites. It collects basic information like page views, referrer, and browser information to help us improve our service. No personal information is collected through our analytics tools.')}
                </p>

                <h3 className="h6 fw-bold mt-3">2.4 {t('collection.userContent.title', 'User-Generated Content')}</h3>
                <p>
                  {t('collection.userContent.p1', 'Our application allows you to create custom button names and counter titles. While we do not intentionally collect personal information through these features, you may choose to enter such information in these fields. We recommend not entering sensitive personal information in any user-generated content. You are responsible for any personal data you choose to include in button names, counter titles, or other user-generated fields.')}
                </p>
              </section>

              <section id={generateAnchorId(3)} className="mb-4">
                <h2 className="h5 fw-bold">3. {t('use.title', 'Use of Data')}</h2>
                <p>{t('use.p1', 'TapDonkey uses the collected data for various purposes:')}</p>
                <ul>
                  <li>{t('use.item1', 'To provide and maintain our service')}</li>
                  <li>{t('use.item2', 'To notify you about changes to our service')}</li>
                  <li>{t('use.item3', 'To provide customer support')}</li>
                  <li>{t('use.item4', 'To gather analysis or valuable information so that we can improve our service')}</li>
                  <li>{t('use.item5', 'To monitor the usage of our service')}</li>
                  <li>{t('use.item6', 'To detect, prevent, and address technical issues')}</li>
                </ul>
              </section>

              <section id={generateAnchorId(4)} className="mb-4">
                <h2 className="h5 fw-bold">4. {t('thirdParty.title', 'Third-Party Service Providers')}</h2>
                <p>
                  {t('thirdParty.p1', 'We may employ third-party companies and individuals to facilitate our Service ("Service Providers"), to provide the Service on our behalf, to perform Service-related services, or to assist us in analyzing how our Service is used.')}
                </p>
                <p>
                  {t('thirdParty.p2', 'These third parties may have access to your Personal Data only to perform these tasks on our behalf and are obligated not to disclose or use it for any other purpose. All of our service providers have been selected with your privacy in mind and operate in compliance with applicable privacy laws, including GDPR.')}
                </p>
                <h3 className="h6 fw-bold mt-3">4.1 {t('thirdParty.hosting.title', 'Hosting and Infrastructure')}</h3>
                <p>
                  {t('thirdParty.hosting.p1', 'We use the following service providers to host and operate our service:')}
                </p>
                <ul>
                  <li>
                    <strong>Vercel</strong> (EU region) - for hosting our web application
                    <br />
                    <span className="small text-body-secondary">Data processor that provides our hosting infrastructure</span>
                    <br />
                    <ExternalLink href="https://vercel.com/legal/privacy-policy" className="small">Privacy Policy</ExternalLink>
                  </li>
                  <li className="mt-2">
                    <strong>MongoDB</strong> (EU region) - for database services
                    <br />
                    <span className="small text-body-secondary">Data processor that stores counter data and related information</span>
                    <br />
                    <ExternalLink href="https://www.mongodb.com/legal/privacy-policy" className="small">Privacy Policy</ExternalLink>
                  </li>
                                    <li className="mt-2">                    <strong>Railway</strong> (EU region) - for backend services                    <br />                    <span className="small text-body-secondary">Data processor that hosts our backend API services</span>                    <br />                    <ExternalLink href="https://railway.app/legal/privacy" className="small">Privacy Policy</ExternalLink>                  </li>
                  <li className="mt-2">
                    <strong>Resend</strong> (EU region) - for email delivery services
                    <br />
                    <span className="small text-body-secondary">Data processor that sends one-time counter link emails</span>
                    <br />
                    <ExternalLink href="https://resend.com/privacy" className="small">Privacy Policy</ExternalLink>
                  </li>
                  {/* Umami section removed as it's no longer used */}
                </ul>
                <p>
                  {t('thirdParty.hosting.p2', 'All of these service providers have been configured to use infrastructure located in the European Union, which helps us meet European data protection requirements.')}
                </p>
              </section>

              <section id={generateAnchorId(5)} className="mb-4">
                <h2 className="h5 fw-bold">5. {t('retention.title', 'Data Retention')}</h2>
                <p>
                  {t('retention.p1', 'We will retain your data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your data to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our policies.')}
                </p>
                <p>
                  {t('retention.p2', 'For counters created with an email address, the email is not stored after the one-time link has been sent. Counter data itself is retained until the counter is deleted or expires.')}
                </p>
              </section>

              <section id={generateAnchorId(6)} className="mb-4">
                <h2 className="h5 fw-bold">6. {t('transfer.title', 'Transfer of Data')}</h2>
                <p>
                  {t('transfer.p1', 'Your information, including Personal Data, may be transferred to — and maintained on — computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ from those of your jurisdiction.')}
                </p>
                <p>
                  {t('transfer.p2', 'Your consent to this Privacy Policy followed by your submission of such information represents your agreement to that transfer.')}
                </p>
                <p>
                  {t('transfer.p3', 'To minimize data transfer concerns, we have configured our service providers to use infrastructure located in the European Union, which provides strong data protection standards.')}
                </p>
              </section>

              <section id={generateAnchorId(7)} className="mb-4">
                <h2 className="h5 fw-bold">7. {t('disclosure.title', 'Disclosure of Data')}</h2>
                <p>{t('disclosure.p1', 'We may disclose your data in the following situations:')}</p>
                <ul>
                  <li>{t('disclosure.item1', 'To comply with a legal obligation')}</li>
                  <li>{t('disclosure.item2', 'To protect and defend the rights or property of TapDonkey')}</li>
                  <li>{t('disclosure.item3', 'To prevent or investigate possible wrongdoing in connection with the service')}</li>
                  <li>{t('disclosure.item4', 'To protect the personal safety of users of the service or the public')}</li>
                  <li>{t('disclosure.item5', 'To protect against legal liability')}</li>
                </ul>
              </section>

              <section id={generateAnchorId(8)} className="mb-4">
                <h2 className="h5 fw-bold">8. {t('security.title', 'Security of Data')}</h2>
                <p>
                  {t('security.p1', 'The security of your data is important to us, but remember that no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your data, we cannot guarantee its absolute security.')}
                </p>
              </section>

              <section id={generateAnchorId(9)} className="mb-4">
                <h2 className="h5 fw-bold">9. {t('rights.title', 'Your Data Protection Rights')}</h2>
                <p>
                  {t('rights.p1', 'If you reside in the European Economic Area (EEA), you have certain data protection rights. TapDonkey aims to take reasonable steps to allow you to correct, amend, delete, or limit the use of your Personal Data.')}
                </p>
                <p>{t('rights.p2', 'You have the following data protection rights:')}</p>
                <ul>
                  <li>{t('rights.item1', 'The right to access, update, or delete the information we have on you')}</li>
                  <li>{t('rights.item2', 'The right of rectification — the right to have your information corrected if it is inaccurate or incomplete')}</li>
                  <li>{t('rights.item3', 'The right to object to our processing of your Personal Data')}</li>
                  <li>{t('rights.item4', 'The right of restriction — the right to request that we restrict the processing of your personal information')}</li>
                  <li>{t('rights.item5', 'The right to data portability — the right to be provided with a copy of your Personal Data in a structured, machine-readable, and commonly used format')}</li>
                  <li>{t('rights.item6', 'The right to withdraw consent at any time where we relied on your consent to process your personal information')}</li>
                </ul>
              </section>

              <section id={generateAnchorId(10)} className="mb-4">
                <h2 className="h5 fw-bold">10. {t('gdpr.title', 'GDPR Data Protection Rights')}</h2>
                <p>
                  {t('gdpr.p1', 'We would like to make sure you are fully aware of all of your data protection rights. Every user is entitled to the following:')}
                </p>
                <ul>
                  <li>{t('gdpr.item1', 'The right to access – You have the right to request copies of your personal data.')}</li>
                  <li>{t('gdpr.item2', 'The right to rectification – You have the right to request that we correct any information you believe is inaccurate. You also have the right to request that we complete information you believe is incomplete.')}</li>
                  <li>{t('gdpr.item3', 'The right to erasure – You have the right to request that we erase your personal data, under certain conditions.')}</li>
                  <li>{t('gdpr.item4', 'The right to restrict processing – You have the right to request that we restrict the processing of your personal data, under certain conditions.')}</li>
                  <li>{t('gdpr.item5', 'The right to object to processing – You have the right to object to our processing of your personal data, under certain conditions.')}</li>
                  <li>{t('gdpr.item6', 'The right to data portability – You have the right to request that we transfer the data that we have collected to another organization, or directly to you, under certain conditions.')}</li>
                </ul>
                <p>
                  {t('gdpr.p2', 'If you make a request, we have one month to respond to you. If you would like to exercise any of these rights, please contact us at our email: ')} 
                  {emailConfig.infoEmail}
                </p>
                <p>
                  {t('gdpr.p3', 'TapDonkey is based in Latvia, a member state of the European Union, and complies with EU data protection laws, including the GDPR.')}
                </p>
              </section>

              <section id={generateAnchorId(11)} className="mb-4">
                <h2 className="h5 fw-bold">11. {t('children.title', 'Children\'s Privacy')}</h2>
                <p>
                  {t('children.p1', 'Our service does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from children under 13. If you are a parent or guardian and you are aware that your child has provided us with personal data, please contact us. If we become aware that we have collected personal data from children without verification of parental consent, we take steps to remove that information from our servers.')}
                </p>
              </section>

              <section id={generateAnchorId(12)} className="mb-4">
                <h2 className="h5 fw-bold">12. {t('changes.title', 'Changes to This Privacy Policy')}</h2>
                <p>
                  {t('changes.p1', 'We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.')}
                </p>
                <p>
                  {t('changes.p2', 'You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.')}
                </p>
              </section>

              <section id={generateAnchorId(13)} className="mb-4">
                <h2 className="h5 fw-bold">13. {t('contact.title', 'Contact Us')}</h2>
                <p>
                  {t('contact.p1', 'If you have any questions about this Privacy Policy, please contact us:')}
                </p>
                <ul>
                  <li>
                    {t('contact.email', 'By email:')} {' '}
                    {emailConfig.infoEmail}
                  </li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy; 